import { IArtField, IArtFieldAnswer } from '../../../services/Contests/interfaces'

/** Default icon per standard field key when the contest hasn't picked one. */
const FIELD_ICON_FALLBACK: Record<string, string> = {
  name: 'lni lni-pencil',
  technique: 'lni lni-brush',
  dimensions: 'lni lni-ruler'
}

/**
 * Resolve the LineIcons class shown next to a field — mirrors the public site's
 * `iconForField` (ArtSubmissionModal) so the panel preview matches the live form.
 * A contest-configured icon always wins; otherwise fall back by key, then type.
 */
export function iconForArtField(field: Pick<IArtField, 'icon' | 'key' | 'type'>): string {
  return (
    (field.icon && field.icon.trim()) ||
    FIELD_ICON_FALLBACK[field.key] ||
    (field.type === 'select' || field.type === 'radio'
      ? 'lni lni-list'
      : field.type === 'checkbox'
        ? 'lni lni-checkmark-circle'
        : 'lni lni-text-format')
  )
}

interface IArtLike {
  technique?: string | null
  dimensions?: string | null
  field_answers?: IArtFieldAnswer[]
}

export interface IResolvedArtMeta {
  /** Stable field key from the contest schema — safe to use as a React key. */
  key: string
  label: string
  value: string
  /** false when the participant left this (optional) field blank. */
  answered: boolean
  show_in_listing: boolean
  show_in_identifier: boolean
  show_in_labels: boolean
}

/** Placeholder shown (styled differently) when an optional field was not filled. */
export const ART_FIELD_NOT_ANSWERED = 'Campo não respondido'

function isAnswered(answer: IArtFieldAnswer): boolean {
  return answer.value !== null && String(answer.value).trim() !== ''
}

/**
 * Format a single answer for display, normalizing checkbox values to Sim/Não.
 */
function formatAnswerValue(answer: IArtFieldAnswer): string {
  if (answer.type === 'checkbox') {
    return answer.value === '1' || answer.value === 'true' ? 'Sim' : 'Não'
  }
  return String(answer.value)
}

/**
 * Resolve the ordered "label: value" pairs to display for an artwork (ABRA-73).
 *
 * Uses the contest's customizable field answers when present (excluding the
 * title, which is shown separately as the heading), and falls back to the
 * legacy technique/dimensions columns for older API responses. Unanswered
 * optional fields are kept (not omitted) and flagged via `answered: false` so
 * callers can render "Campo não respondido" distinctly.
 */
export function resolveArtMeta(art: IArtLike): IResolvedArtMeta[] {
  if (art.field_answers && art.field_answers.length > 0) {
    return art.field_answers
      .filter((answer) => answer.key !== 'name')
      .map((answer) => {
        const answered = isAnswered(answer)
        return {
          key: answer.key,
          label: answer.label,
          value: answered ? formatAnswerValue(answer) : ART_FIELD_NOT_ANSWERED,
          answered,
          show_in_listing: !!answer.show_in_listing,
          show_in_identifier: !!answer.show_in_identifier,
          show_in_labels: !!answer.show_in_labels
        }
      })
  }

  // Legacy fallback (API without field_answers): keep showing these everywhere.
  const meta: IResolvedArtMeta[] = []
  const legacyFlags = { show_in_listing: true, show_in_identifier: true, show_in_labels: true }
  if (art.technique) {
    meta.push({ key: 'technique', label: 'Técnica e suporte', value: art.technique, answered: true, ...legacyFlags })
  }
  if (art.dimensions) {
    meta.push({ key: 'dimensions', label: 'Dimensões', value: art.dimensions, answered: true, ...legacyFlags })
  }
  return meta
}
